/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { ZodIssue } from 'zod';
import { accountResponseSchemas } from './accountResponseSchemas';
import type { AccountResponseSchemaKey } from './responseSchemaKeys';
import { reportSchemaMismatch, type MismatchSeverity } from './schemaReporter';
import { templatizeEndpoint, type ResponseOrigin } from './templatizeEndpoint';

/**
 * Reports, never rejects: a drifted response is reported and handed back
 * untouched, so a cosmetic API change cannot break an account screen.
 */

/** Enough issues to identify the drift; a wholly wrong payload yields dozens. */
const MAX_REPORTED_ISSUES = 6;

/**
 * Bounds the dedupe map, which is keyed by distinct drift rather than request.
 * Past it, the least recently seen signature is evicted.
 */
const MAX_TRACKED_SIGNATURES = 200;

/**
 * Type names only, never values: `invalid_type` reports `expected`/`received`,
 * which are type names, while every other code can embed the offending value in
 * its message. `issue.message` is never used.
 */
const describeIssue = (issue: ZodIssue): string => {
  const path = issue.path.join('.') || '(root)';

  if (issue.code === 'invalid_type') {
    return `${path}: expected ${issue.expected}, got ${issue.received}`;
  }

  return `${path}: ${issue.code}`;
};

const severityOf = (issues: readonly ZodIssue[]): MismatchSeverity =>
  issues.some((issue) => issue.path.length === 0)
    ? 'response-shape'
    : 'field-drift';

/** 1, 10, 100, 1000 … so recurring drift shows its scale without flooding. */
const isReportableOccurrence = (count: number): boolean =>
  /^10*$/.test(String(count));

const occurrences = new Map<string, number>();

/**
 * Re-inserting moves a key to the end of the Map, so the first key is the
 * least recently seen one. Dropping the new signature instead would leave it
 * untracked at count 1, which reports on every single occurrence.
 */
const track = (signature: string, count: number): void => {
  if (occurrences.has(signature)) {
    occurrences.delete(signature);
  } else if (occurrences.size >= MAX_TRACKED_SIGNATURES) {
    const coldest = occurrences.keys().next().value;

    if (coldest !== undefined) {
      occurrences.delete(coldest);
    }
  }

  occurrences.set(signature, count);
};

/** Test seam: the dedupe map is module state and outlives a single test. */
export function resetSchemaMismatchTracking(): void {
  occurrences.clear();
}

/**
 * Checks `data` against the schema registered for `key` and returns it
 * unchanged, reporting when it no longer matches.
 *
 * Each distinct drift — same endpoint, same schema, same set of issues — is
 * reported on its 1st, 10th, 100th … occurrence. Without that, a drifted
 * profile refetched on every screen would log identically hundreds of times per
 * session and bury everything else.
 */
export function validateAccountResponse<T>(
  key: AccountResponseSchemaKey,
  origin: ResponseOrigin,
  data: T,
): T {
  const result = accountResponseSchemas[key].safeParse(data);

  if (result.success) {
    return data;
  }

  const { issues } = result.error;
  const endpoint = templatizeEndpoint(origin);
  const severity = severityOf(issues);
  const described = issues.slice(0, MAX_REPORTED_ISSUES).map(describeIssue);
  const summary = described.join('; ');

  const signature = `${key}|${endpoint}|${severity}|${summary}`;
  const count = (occurrences.get(signature) ?? 0) + 1;

  track(signature, count);

  if (!isReportableOccurrence(count)) {
    return data;
  }

  const omitted = issues.length - described.length;

  reportSchemaMismatch({
    key,
    endpoint,
    severity,
    issues: omitted > 0 ? [...described, `(+${omitted} more)`] : described,
    issueCount: issues.length,
    occurrences: count,
  });

  return data;
}
